import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Plus, X, GripVertical, Calendar, User, Link2, Trash2, Loader2, Bell, Search, CheckSquare, Square, Sparkles } from 'lucide-react';
import { API_URL } from '../config';



const COLUMNS = [
  { key: 'new', color: '#3b82f6', bg: '#eff6ff' },
  { key: 'urgent', color: '#ef4444', bg: '#fef2f2' },
  { key: 'in_progress', color: '#f97316', bg: '#fff7ed' },
  { key: 'later', color: '#8b5cf6', bg: '#f5f3ff' },
  { key: 'done', color: '#22c55e', bg: '#f0fdf4' },
];

const colLabels = {
  ru: { new: 'Новые', urgent: 'Срочные', in_progress: 'В работе', later: 'Попозже', done: 'Готово' },
  kz: { new: 'Жаңа', urgent: 'Шұғыл', in_progress: 'Жұмыста', later: 'Кейінірек', done: 'Дайын' },
  en: { new: 'New', urgent: 'Urgent', in_progress: 'In Progress', later: 'Later', done: 'Done' },
};

const TaskBoard = ({ token, userRole, userLogin }) => {
  const { language } = useLanguage();
  const labels = colLabels[language] || colLabels.ru;
  const [tasks, setTasks] = useState([]);
  const [managers, setManagers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', assignee_id: '', deadline: '', lead_id: '', reminder_at: '' });
  const [saving, setSaving] = useState(false);
  const [dragTask, setDragTask] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [leadSearch, setLeadSearch] = useState('');
  const [newSubtask, setNewSubtask] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [editingSubtask, setEditingSubtask] = useState(null);
  const [editingSubtaskText, setEditingSubtaskText] = useState('');
  const [staffFilter, setStaffFilter] = useState('');  // super-admin only
  const isSuperAdmin = userLogin === 'admin';

  const authHeaders = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchTasks = useCallback(async () => {
    try {
      const url = staffFilter ? `${API_URL}/api/tasks?user_id=${encodeURIComponent(staffFilter)}` : `${API_URL}/api/tasks`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setTasks(await res.json());
    } catch (e) { console.error('fetchTasks error:', e); }
    setLoading(false);
  }, [token, staffFilter]);

  const fetchManagers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/leads/managers`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setManagers(await res.json());
    } catch {}
  }, [token]);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/leads`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setLeads(Array.isArray(data) ? data.slice(0, 50) : []); }
    } catch {}
  }, [token]);

  useEffect(() => { fetchTasks(); fetchManagers(); fetchLeads(); }, [fetchTasks, fetchManagers, fetchLeads]);

  const updateTask = async (id, data) => {
    await fetch(`${API_URL}/api/tasks/${id}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(data) });
    fetchTasks();
  };

  const deleteTask = async (id) => {
    await fetch(`${API_URL}/api/tasks/${id}`, { method: 'DELETE', headers: authHeaders });
    fetchTasks();
  };

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    await fetch(`${API_URL}/api/tasks`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ ...form, status: createModal }) });
    setCreateModal(null);
    setForm({ title: '', description: '', assignee_id: '', deadline: '', lead_id: '', reminder_at: '' });
    setLeadSearch('');
    fetchTasks();
    setSaving(false);
  };

  const handleEdit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    await updateTask(editModal, form);
    setEditModal(null);
    setSaving(false);
  };

  const openEdit = (task) => {
    setForm({ title: task.title, description: task.description || '', assignee_id: task.assignee_id || '', deadline: task.deadline || '', lead_id: task.lead_id || '', reminder_at: task.reminder_at || '' });
    setLeadSearch('');
    setNewSubtask('');
    setEditModal(task.id);
  };

  const addSubtask = async (taskId) => {
    if (!newSubtask.trim()) return;
    await fetch(`${API_URL}/api/tasks/${taskId}/subtasks`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ title: newSubtask.trim() }) });
    setNewSubtask('');
    fetchTasks();
  };

  const toggleSubtask = async (taskId, subtaskId) => {
    await fetch(`${API_URL}/api/tasks/${taskId}/subtasks/${subtaskId}`, { method: 'PUT', headers: authHeaders });
    fetchTasks();
  };

  const deleteSubtask = async (taskId, subtaskId) => {
    await fetch(`${API_URL}/api/tasks/${taskId}/subtasks/${subtaskId}`, { method: 'DELETE', headers: authHeaders });
    fetchTasks();
  };

  const renameSubtask = async (taskId, subtaskId, newTitle) => {
    if (!newTitle.trim()) return;
    await fetch(`${API_URL}/api/tasks/${taskId}/subtasks/${subtaskId}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ title: newTitle.trim() }) });
    setEditingSubtask(null);
    setEditingSubtaskText('');
    fetchTasks();
  };

  const generateAiChecklist = async (taskId) => {
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/${taskId}/ai-checklist`, { method: 'POST', headers: jsonHeaders });
      if (res.ok) fetchTasks();
      else { const err = await res.json(); alert(err.detail || 'AI error'); }
    } catch { alert('AI error'); }
    setAiLoading(false);
  };

  const [quickInput, setQuickInput] = useState({});

  const handleQuickAdd = async (colKey) => {
    const title = (quickInput[colKey] || '').trim();
    if (!title) return;
    await fetch(`${API_URL}/api/tasks`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ title, status: colKey }) });
    setQuickInput(p => ({...p, [colKey]: ''}));
    fetchTasks();
  };

  // Drag & Drop
  const onDragStart = (e, taskId) => {
    setDragTask(taskId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e, colKey) => {
    e.preventDefault();
    setDragOver(colKey);
  };
  const onDrop = (e, colKey) => {
    e.preventDefault();
    if (dragTask) {
      updateTask(dragTask, { status: colKey });
    }
    setDragTask(null);
    setDragOver(null);
  };
  const onDragEnd = () => { setDragTask(null); setDragOver(null); };

  const isOverdue = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date() && new Date(deadline).toDateString() !== new Date().toDateString();
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#8b4444]" /></div>;

  return (
    <div className="space-y-4" data-testid="task-board">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-2xl font-bold text-[#061d36]">{language === 'ru' ? 'Задачник' : 'Tasks'}</h2>
          {isSuperAdmin && (
            <div className="flex items-center gap-2" data-testid="staff-filter-wrap">
              <label className="text-xs text-[#061d36]/60 font-medium">
                {language === 'ru' ? 'Сотрудник:' : 'Employee:'}
              </label>
              <select
                data-testid="task-staff-filter"
                value={staffFilter}
                onChange={e => setStaffFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-[#061d36]/15 rounded-lg bg-white text-[#061d36] focus:outline-none focus:border-[#8b4444]"
              >
                <option value="">{language === 'ru' ? 'Все сотрудники' : 'All employees'}</option>
                {managers.map(m => (
                  <option key={m.id} value={m.id}>{m.company_name || m.login}</option>
                ))}
              </select>
              {staffFilter && (
                <button
                  data-testid="task-staff-filter-clear"
                  onClick={() => setStaffFilter('')}
                  className="text-xs px-2 py-1 text-[#8b4444] hover:bg-[#8b4444]/10 rounded transition-colors"
                >
                  ✕ {language === 'ru' ? 'сбросить' : 'clear'}
                </button>
              )}
            </div>
          )}
        </div>
        <span className="text-sm text-[#061d36]/50">{tasks.length} {language === 'ru' ? 'задач' : 'tasks'}</span>
      </div>

      {/* Kanban columns */}
      <div className="flex gap-4 overflow-x-auto pb-2" style={{ minHeight: '65vh' }}>
        {COLUMNS.map(col => {
          const colTasks = tasks.filter(t => t.status === col.key);
          const isDragTarget = dragOver === col.key;
          return (
            <div key={col.key}
              className={`flex-1 min-w-[240px] rounded-2xl transition-all duration-200 ${isDragTarget ? 'scale-[1.02] shadow-lg' : ''}`}
              style={{ backgroundColor: isDragTarget ? '#0d2a47' : '#061d36' }}
              onDragOver={(e) => onDragOver(e, col.key)}
              onDrop={(e) => onDrop(e, col.key)}
              onDragLeave={() => setDragOver(null)}
              data-testid={`col-${col.key}`}>

              {/* Column header */}
              <div className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: col.color }} />
                  <span className="text-[13px] font-bold text-white tracking-wide uppercase">{labels[col.key]}</span>
                  {colTasks.length > 0 && (
                    <span className="text-[11px] text-white/30 font-medium tabular-nums">{colTasks.length}</span>
                  )}
                </div>
                <button onClick={() => { setForm({ title: '', description: '', assignee_id: '', deadline: '', lead_id: '', reminder_at: '' }); setLeadSearch(''); setCreateModal(col.key); }}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold hover:bg-white/15 transition-all"
                  data-testid={`add-task-${col.key}`}>
                  <Plus className="w-5 h-5" strokeWidth={2.5} />
                </button>
              </div>

              {/* Tasks */}
              <div className="px-2.5 pb-3 space-y-2">
                {colTasks.map(task => (
                  <div key={task.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, task.id)}
                    onDragEnd={onDragEnd}
                    onClick={() => openEdit(task)}
                    className={`bg-white/95 rounded-xl px-3.5 py-3 cursor-grab active:cursor-grabbing hover:bg-white hover:shadow-md transition-all duration-150 group border ${task.priority ? 'border-l-[3px] border-l-[#8b4444] border-t-white/10 border-r-white/10 border-b-white/10' : 'border-white/10'} ${dragTask === task.id ? 'opacity-30 scale-95' : 'shadow-sm'}`}
                    data-testid={`task-${task.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-[#061d36] leading-snug">{task.title}</p>
                        {task.description && <p className="text-[11px] text-[#061d36]/35 mt-1.5 line-clamp-2 leading-relaxed">{task.description}</p>}
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); updateTask(task.id, { priority: !task.priority }); }}
                        className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center transition-all hover:scale-110"
                        data-testid={`priority-${task.id}`}>
                        <img src="/logo-icon-dark.png" alt="" className="w-4 h-4 transition-all"
                          style={task.priority
                            ? { filter: 'brightness(0) saturate(100%) invert(25%) sepia(50%) saturate(900%) hue-rotate(320deg) brightness(0.7)' }
                            : { opacity: 0.15 }
                          } />
                      </button>
                    </div>
                    {(task.assignee_name || task.deadline || task.lead_name || task.reminder_at || (task.subtasks && task.subtasks.length > 0)) && (
                      <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                        {task.subtasks && task.subtasks.length > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-[#061d36]/40 bg-[#061d36]/[0.03] px-2 py-0.5 rounded-full">
                            <CheckSquare className="w-2.5 h-2.5" />{task.subtasks.filter(s => s.done).length}/{task.subtasks.length}
                          </span>
                        )}
                        {task.assignee_name && (
                          <span className="flex items-center gap-1 text-[10px] text-[#061d36]/40 bg-[#061d36]/[0.03] px-2 py-0.5 rounded-full">
                            <User className="w-2.5 h-2.5" />{task.assignee_name}
                          </span>
                        )}
                        {task.deadline && (
                          <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold text-white ${isOverdue(task.deadline) ? 'bg-red-700' : 'bg-[#8b4444]'}`}>
                            <Calendar className="w-2.5 h-2.5" />{new Date(task.deadline).toLocaleDateString()}
                          </span>
                        )}
                        {task.lead_name && (
                          <span className="flex items-center gap-1 text-[10px] text-[#8b4444]/60 bg-[#8b4444]/[0.04] px-2 py-0.5 rounded-full">
                            <Link2 className="w-2.5 h-2.5" />{task.lead_name.split('/')[0].trim()}
                          </span>
                        )}
                        {task.reminder_at && (
                          <span className="flex items-center gap-1 text-[10px] text-amber-500/70 bg-amber-50/50 px-2 py-0.5 rounded-full">
                            <Bell className="w-2.5 h-2.5" />{new Date(task.reminder_at).toLocaleString([], {month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'})}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {colTasks.length === 0 && !quickInput[col.key] && (
                  <div className="flex items-center justify-center py-8">
                    <span className="text-[11px] text-white/15">{language === 'ru' ? 'Перетащите сюда' : 'Drop here'}</span>
                  </div>
                )}

                {/* Quick add */}
                <div className="mt-1">
                  <input
                    value={quickInput[col.key] || ''}
                    onChange={e => setQuickInput(p => ({...p, [col.key]: e.target.value}))}
                    onKeyDown={e => { if (e.key === 'Enter') handleQuickAdd(col.key); }}
                    placeholder={language === 'ru' ? '+ Быстрая задача...' : '+ Quick task...'}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/20 focus:outline-none focus:bg-white/10 focus:border-white/20 transition-all"
                    data-testid={`quick-add-${col.key}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create / Edit Modal */}
      {(createModal || editModal) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onMouseDown={e => { if (e.target === e.currentTarget) { setCreateModal(null); setEditModal(null); } }}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#061d36]">
                {editModal ? (language === 'ru' ? 'Редактировать задачу' : 'Edit Task') : (language === 'ru' ? 'Новая задача' : 'New Task')}
              </h3>
              {editModal && (
                <button onClick={() => { deleteTask(editModal); setEditModal(null); }}
                  className="text-red-400 hover:text-red-600 transition-colors" data-testid="delete-task">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Название' : 'Title'} *</label>
                <input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))}
                  className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]"
                  placeholder={language === 'ru' ? 'Что нужно сделать?' : 'What needs to be done?'}
                  autoFocus />
              </div>
              <div>
                <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Описание' : 'Description'}</label>
                <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))}
                  rows={5} className="w-full px-3 py-2.5 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444] resize-y min-h-[110px]"
                  placeholder={language === 'ru' ? 'Детали задачи...' : 'Task details...'} />
              </div>
              {/* Subtasks / Checklist — only in edit mode */}
              {editModal && (() => {
                const currentTask = tasks.find(t => t.id === editModal);
                const subtasks = currentTask?.subtasks || [];
                return (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-[#061d36] font-semibold flex items-center gap-1.5">
                        <CheckSquare className="w-3.5 h-3.5 text-[#061d36]/50" />
                        {language === 'ru' ? 'Подзадачи' : 'Subtasks'}
                        {subtasks.length > 0 && <span className="text-[#061d36]/30">{subtasks.filter(s => s.done).length}/{subtasks.length}</span>}
                      </label>
                      <button onClick={() => generateAiChecklist(editModal)} disabled={aiLoading}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold text-[#8b4444] bg-[#8b4444]/5 hover:bg-[#8b4444]/10 transition-all disabled:opacity-50"
                        data-testid="ai-checklist-btn">
                        {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {aiLoading ? 'AI...' : 'AI'}
                      </button>
                    </div>
                    {subtasks.length > 0 && (
                      <div className="space-y-1 mb-2 max-h-40 overflow-y-auto">
                        {subtasks.map(st => (
                          <div key={st.id} className="flex items-center gap-2 group">
                            <button onClick={() => toggleSubtask(editModal, st.id)} className="flex-shrink-0 text-[#061d36]/40 hover:text-[#8b4444] transition-colors">
                              {st.done ? <CheckSquare className="w-4 h-4 text-[#8b4444]" /> : <Square className="w-4 h-4" />}
                            </button>
                            {editingSubtask === st.id ? (
                              <input value={editingSubtaskText} onChange={e => setEditingSubtaskText(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') renameSubtask(editModal, st.id, editingSubtaskText); if (e.key === 'Escape') setEditingSubtask(null); }}
                                onBlur={() => renameSubtask(editModal, st.id, editingSubtaskText)}
                                autoFocus
                                className="flex-1 text-xs px-2 py-0.5 border border-[#8b4444]/30 rounded focus:outline-none focus:border-[#8b4444]" />
                            ) : (
                              <span onClick={(e) => { e.stopPropagation(); setEditingSubtask(st.id); setEditingSubtaskText(st.title); }}
                                className={`flex-1 text-xs cursor-text hover:bg-[#061d36]/[0.02] px-1 rounded ${st.done ? 'line-through text-[#061d36]/25' : 'text-[#061d36]/70'}`}>
                                {st.title}
                              </span>
                            )}
                            <button onClick={() => deleteSubtask(editModal, st.id)}
                              className="opacity-0 group-hover:opacity-100 text-[#061d36]/20 hover:text-red-400 transition-all flex-shrink-0">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input value={newSubtask} onChange={e => setNewSubtask(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addSubtask(editModal); }}
                        placeholder={language === 'ru' ? 'Добавить подзадачу...' : 'Add subtask...'}
                        className="flex-1 px-3 py-1.5 border border-[#061d36]/10 rounded-lg text-xs focus:outline-none focus:border-[#8b4444]" />
                      <button onClick={() => addSubtask(editModal)} disabled={!newSubtask.trim()}
                        className="px-2 py-1.5 bg-[#061d36] text-white rounded-lg text-xs font-medium hover:bg-[#0d2a47] disabled:opacity-30 transition-all">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Исполнитель' : 'Assignee'}</label>
                  <select value={form.assignee_id} onChange={e => setForm(p => ({...p, assignee_id: e.target.value}))}
                    className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444] bg-white">
                    <option value="">{language === 'ru' ? 'Не назначен' : 'Unassigned'}</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.company_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Дедлайн' : 'Deadline'}</label>
                  <input type="date" value={form.deadline} onChange={e => setForm(p => ({...p, deadline: e.target.value}))}
                    className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Привязка к лиду' : 'Link to Lead'}</label>
                {form.lead_id ? (
                  <div className="flex items-center gap-2">
                    <span className="flex-1 px-3 py-2 bg-[#8b4444]/5 border border-[#8b4444]/20 rounded-lg text-sm text-[#061d36]">
                      <Link2 className="w-3 h-3 inline mr-1.5 text-[#8b4444]" />
                      {leads.find(l => l.id === form.lead_id)?.name || form.lead_id}
                    </span>
                    <button onClick={() => setForm(p => ({...p, lead_id: ''}))}
                      className="text-[#061d36]/30 hover:text-red-500 transition-colors"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#061d36]/30" />
                    <input value={leadSearch} onChange={e => setLeadSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]"
                      placeholder={language === 'ru' ? 'Поиск по имени или телефону...' : 'Search by name or phone...'} />
                    {leadSearch.length >= 2 && (
                      <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[#061d36]/15 rounded-lg shadow-xl max-h-40 overflow-y-auto">
                        {leads.filter(l => {
                          const s = leadSearch.toLowerCase();
                          return (l.name||'').toLowerCase().includes(s) || (l.phone||'').includes(s) || (l.city||'').toLowerCase().includes(s);
                        }).slice(0, 8).map(l => (
                          <button key={l.id} onClick={() => { setForm(p => ({...p, lead_id: l.id})); setLeadSearch(''); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-[#8b4444]/5 border-b border-[#061d36]/5 last:border-b-0 transition-colors">
                            <span className="font-medium text-[#061d36]">{l.name}</span>
                            <span className="text-[#061d36]/40 ml-2 text-xs">{l.phone}</span>
                          </button>
                        ))}
                        {leads.filter(l => { const s = leadSearch.toLowerCase(); return (l.name||'').toLowerCase().includes(s) || (l.phone||'').includes(s); }).length === 0 && (
                          <p className="px-3 py-2 text-xs text-[#061d36]/30">{language === 'ru' ? 'Не найдено' : 'Not found'}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs text-[#061d36] font-semibold mb-1 block flex items-center gap-1.5">
                  <Bell className="w-3.5 h-3.5 text-[#8b4444]" />{language === 'ru' ? 'Напоминание' : 'Reminder'}
                </label>
                <input type="datetime-local" value={form.reminder_at} onChange={e => setForm(p => ({...p, reminder_at: e.target.value}))}
                  className="w-full px-3 py-2 border border-[#8b4444]/20 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
              </div>

            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setCreateModal(null); setEditModal(null); }}
                className="flex-1 py-2.5 text-sm text-[#061d36]/50 hover:text-[#061d36] transition-colors border border-[#061d36]/10 rounded-lg">
                {language === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
              <button onClick={editModal ? handleEdit : handleCreate} disabled={saving || !form.title.trim()}
                data-testid="save-task-btn"
                className="flex-1 py-2.5 bg-[#061d36] text-white rounded-lg text-sm font-semibold hover:bg-[#0d2a47] disabled:opacity-50 transition-all">
                {saving ? '...' : editModal ? (language === 'ru' ? 'Сохранить' : 'Save') : (language === 'ru' ? 'Создать' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskBoard;
